import { DialogDisclosure, Heading, HeadingLevel } from "@ariakit/react";
import { DialogTrigger } from "@indietabletop/appkit";
import { button } from "../../common/common.css.ts";
import { database } from "../../database.ts";
import { EditMode } from "../../engine/EditMode.ts";
import { GitWeapon } from "../../engine/Weapon.ts";
import { FailureView } from "../failures/FailureView.tsx";
import { PremiumContentNote } from "../PremiumContentNote/PremiumContentNote.tsx";
import { ModifyAttitudeDialog } from "../StepperDialog/StepperDialog.tsx";
import { ChooseGearDialog } from "./ChooseGearDialog.tsx";
import { ChooseTreatmentsDialog } from "./ChooseTreatmentsDialog.tsx";
import { ChooseGitWeaponsDialog } from "./ChooseWeaponsDialog.tsx";
import { InMemoriamView } from "./InMemoriam.tsx";
import { IssuesList } from "./IssueCard.tsx";
import { ItemNotFound } from "./ItemNotFound.tsx";
import { LoadoutItem } from "./LoadoutItem.tsx";
import { ModelContext, useGitContext } from "./ModelContext.tsx";
import { ModelCostCap } from "./ModelCostCap.tsx";
import { ProfileInfo } from "./ProfileInfo.tsx";
import { RebootButton } from "./RebootButton.tsx";
import { common, gang, modifyIndicator } from "./style.css.ts";
import { useGangContext } from "./useGang.ts";
import { useGit } from "./useGit.tsx";
import { WeaponsSection } from "./WeaponsSection.tsx";

function GitActions() {
  const git = useGitContext();

  return (
    <div className={common.actions}>
      {git.can(EditMode.ADD, "weapons") && (
        <DialogTrigger>
          <ChooseGitWeaponsDialog />
          <DialogDisclosure
            className={button({
              size: "small",
              maxInlineSize: "auto",
            })}
          >
            Choose weapons
          </DialogDisclosure>
        </DialogTrigger>
      )}

      {git.can(EditMode.ADD, "gear") && (
        <DialogTrigger>
          <ChooseGearDialog />
          <DialogDisclosure
            className={button({
              theme: "beige",
              size: "small",
              maxInlineSize: "auto",
            })}
          >
            Choose gear
          </DialogDisclosure>
        </DialogTrigger>
      )}

      {git.can(EditMode.ADD, "treatments") && (
        <DialogTrigger>
          <ChooseTreatmentsDialog />
          <DialogDisclosure
            className={button({
              theme: "beige",
              size: "small",
              maxInlineSize: "auto",
            })}
          >
            Record treatment
          </DialogDisclosure>
        </DialogTrigger>
      )}
    </div>
  );
}

function ModifyAttitudeButton() {
  const { gang } = useGangContext();

  if (!gang.mode.isCampaignMode) {
    return null;
  }

  return (
    <DialogTrigger>
      <ModifyAttitudeDialog />
      <DialogDisclosure className={modifyIndicator.button}>+</DialogDisclosure>
    </DialogTrigger>
  );
}

function GitProfileInfo() {
  const git = useGitContext();

  return (
    <ProfileInfo
      name={git.name}
      type={
        git.type.id === git.originalType.id
          ? git.type.name
          : `${git.type.name} (${git.originalType.name})`
      }
      stats={[
        { label: "Scoot", value: git.scoot },
        { label: "Headbutt", value: git.headbutt },
        { label: "Toughness", value: git.toughness },
        {
          label: "Attitude",
          value: git.attitude,
          action: <ModifyAttitudeButton />,
        },
      ]}
      cap={<ModelCostCap model={git} />}
    />
  );
}

function GitSpecialRules() {
  const git = useGitContext();
  const { gang, specialRules } = git;
  const showCosts = gang.mode.itemCostVisibility === "VISIBLE";

  if (specialRules.length === 0) {
    return null;
  }

  return (
    <div>
      <Heading className={common.sectionHeading}>Special</Heading>

      <HeadingLevel>
        <div className={common.itemsList}>
          {specialRules.map((item, index) => {
            switch (item.type) {
              case "FACTION_RULE":
              case "RULE": {
                if (item.lookup.isFailure) {
                  return (
                    <ItemNotFound
                      key={index}
                      result={item.lookup}
                      item="a special rule"
                      onRemove={null}
                    />
                  );
                }

                const rule = item.lookup.value;
                return (
                  <LoadoutItem
                    key={rule.id}
                    item={{
                      name: rule.name,
                      description: rule.description,
                    }}
                    onRemove={null}
                    type={
                      item.type === "FACTION_RULE"
                        ? "Faction Rule"
                        : "Special Rule"
                    }
                    premium={item.type === "FACTION_RULE"}
                  />
                );
              }

              case "GEAR": {
                const removeGear = async () => {
                  const nextGit = git.removeGear(item.index);
                  await database.gangs.updateOne(nextGit.gang.data);
                };

                const handleRemove = git.can(EditMode.REMOVE_FROM_LIST, "gear")
                  ? removeGear
                  : null;

                if (item.lookup.isFailure) {
                  return (
                    <ItemNotFound
                      key={index}
                      result={item.lookup}
                      item="fancy gear"
                      onRemove={handleRemove}
                    />
                  );
                }

                const gear = item.lookup.value;
                return (
                  <LoadoutItem
                    key={gear.id}
                    item={{
                      name: gear.name,
                      description: gear.description,
                      cost: showCosts ? gear.cost : null,
                    }}
                    type="Fancy Gear"
                    onRemove={handleRemove}
                    premium
                  />
                );
              }

              case "TREATMENT": {
                const removeTreatment = async () => {
                  const nextGit = git.removeTreatment(item.index);
                  await database.gangs.updateOne(nextGit.gang.data);
                };

                const handleRemove = git.can(
                  EditMode.REMOVE_FROM_LIST,
                  "treatments",
                )
                  ? removeTreatment
                  : null;

                if (item.lookup.isFailure) {
                  return (
                    <ItemNotFound
                      key={index}
                      result={item.lookup}
                      item="a treatment"
                      onRemove={handleRemove}
                    />
                  );
                }

                const treatment = item.lookup.value;
                return (
                  <LoadoutItem
                    key={treatment.id}
                    type={`${treatment.location.name} Treatment`}
                    item={{
                      name: treatment.name,
                      description: treatment.description,
                    }}
                    onRemove={handleRemove}
                    extra={treatment.id === "reboot_button" && <RebootButton />}
                    premium
                  />
                );
              }
            }
          })}
        </div>
      </HeadingLevel>
    </div>
  );
}

function GitWeaponsSection() {
  const git = useGitContext();

  return (
    <WeaponsSection
      headbutt={GitWeapon.fromHeadbutt(git)}
      weapons={git.weapons}
      weaponSpecialRules={git.weaponSpecialRules}
      onRemoveWeapon={(weapon) => {
        const nextGit = git.removeWeapon(weapon.index);
        void database.gangs.updateOne(nextGit.gang.data);
      }}
    />
  );
}

export function GitView() {
  const { lookup, readOnly } = useGit();

  if (lookup.isFailure) {
    return <FailureView failure={lookup.failure} />;
  }

  const git = lookup.value;
  return (
    <ModelContext value={git}>
      {git.causeOfDeath ? (
        <InMemoriamView reason={git.causeOfDeath} />
      ) : (
        <div className={gang.container}>
          <GitProfileInfo />

          <HeadingLevel>
            <div className={common.sectionsStack}>
              <IssuesList />

              <GitWeaponsSection />

              <GitSpecialRules />
            </div>
          </HeadingLevel>

          {readOnly ? (
            git.hasPremiumContent && <PremiumContentNote />
          ) : (
            <GitActions />
          )}
        </div>
      )}
    </ModelContext>
  );
}
